import update from 'immutability-helper';
import PropTypes from 'prop-types';
import React, { useContext, useState } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import styled from 'styled-components';
import { PATH_KEY_TO_POSITION } from '../../constants';
import {
  DispatchContext,
  StateContext,
} from '../../contexts/use-menu-editor-context';
import AddButton from '../shared/AddButton';
import BottomControls from '../shared/BottomControls';
import ProductEditor from './product-editor';
import ProductItem from './product-item';

const LEVEL = PATH_KEY_TO_POSITION['productIdx'];

const ListContainer = styled.div``;

function ProductList(props) {
  const {
    parent,
    menuIdx,
    categoryIdx,
    setIsEditMode,
    isEditMode,
    isExpanded,
    setIsExpanded,
  } = props;

  const [showProductEditor, setShowProductEditor] = useState(false);
  const toggleEditor = () => {
    setShowProductEditor(!showProductEditor);
    setIsEditMode(!showProductEditor);
  };

  const [isUpdatingProductOrder, setIsUpdatingProductOrder] = useState(false);

  const context = useContext(StateContext);
  const dispatch = useContext(DispatchContext);

  const {
    state: { menus },
    client,
  } = context;

  const setProducts = async newProducts => {
    const menusCloned = [...menus];
    menusCloned[menuIdx].children[categoryIdx].children = newProducts;

    dispatch({ type: 'FETCH_MENUS_RESPONSE', payload: menusCloned });
    setIsUpdatingProductOrder(true);

    try {
      const productPositions = newProducts.map(newProduct => {
        const menuObj = {
          state: 'reposition',
          menu: {
            id: newProduct.id,
            position: newProducts.indexOf(newProduct),
          },
        };
        return menuObj;
      });

      await client.post('/menu-operations', productPositions);
    } catch (err) {
      const oldMenus = [...menus];
      console.log(err);
      dispatch({ type: 'FETCH_MENUS_RESPONSE', payload: oldMenus });
    } finally {
      setIsUpdatingProductOrder(false);
    }
  };

  const reorderCards = result => {
    // dropped outside the list
    if (!result.destination) {
      return;
    }

    if (result.source && !!result.destination) {
      if (result.destination.index === result.source.index) {
        return;
      }
      const dragCard = parent.children[result.source.index];
      const newProducts = update(parent.children, {
        $splice: [
          [result.source.index, 1],
          [result.destination.index, 0, dragCard],
        ],
      });
      setProducts(newProducts);
    }
  };

  const grid = 0;
  const getItemStyle = (isDragging, draggableStyle) => ({
    // some basic styles to make the items look a bit nicer
    userSelect: 'none',
    padding: 0 * grid,
    margin: `0 0 ${grid}px 0`,

    // change background colour if dragging
    background: 'white',

    // styles we need to apply on draggables
    ...draggableStyle,
  });

  const isAnyChildExpanded = (parent.children || []).some(product => {
    return Boolean(isExpanded[product.id]);
  });

  return (
    <ListContainer>
      <DragDropContext onDragEnd={reorderCards}>
        <Droppable droppableId="droppable-product">
          {(provided, snapshot) => (
            <div {...provided.droppableProps} ref={provided.innerRef}>
              {(parent.children || []).map((product, idx) => {
                return (
                  <Draggable
                    key={product.id}
                    draggableId={product.id}
                    index={idx}
                    isDragDisabled={
                      isUpdatingProductOrder ||
                      showProductEditor ||
                      isEditMode ||
                      isAnyChildExpanded
                    }
                  >
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        style={getItemStyle(
                          snapshot.isDragging,
                          provided.draggableProps.style
                        )}
                      >
                        <ProductItem
                          key={`product-item-${product.id}`}
                          menuIdx={menuIdx}
                          categoryIdx={categoryIdx}
                          productIdx={idx}
                          product={product}
                          parent={parent}
                          setIsEditMode={setIsEditMode}
                          isEditMode={isEditMode}
                          isExpanded={isExpanded}
                          setIsExpanded={setIsExpanded}
                          isAnyChildItemExpanded={isAnyChildExpanded}
                        />
                      </div>
                    )}
                  </Draggable>
                );
              })}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
      {showProductEditor ? (
        <ProductEditor
          parent={parent}
          menuIdx={menuIdx}
          categoryIdx={categoryIdx}
          onCancel={toggleEditor}
          setShowEditor={setShowProductEditor}
        />
      ) : (
        <BottomControls level={LEVEL}>
          <AddButton onClick={toggleEditor}>Add a Product</AddButton>
        </BottomControls>
      )}
    </ListContainer>
  );
}

ProductList.propTypes = {
  menuIdx: PropTypes.number.isRequired,
  categoryIdx: PropTypes.number.isRequired,
  parent: PropTypes.object.isRequired,
};

export default ProductList;



// WEBPACK FOOTER //
// ./src/components/Dashboard/Panels/MenuEditor/components/ProductList/product-list.jsx